import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { usePermissions } from 'commons/customHooks/useAuth'
import { getLastDayOfMonth } from 'commons/utils/date'
import { numberToDecimalString } from 'commons/utils/number'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { isValidNumber } from 'commons/utils/string'
import {
  Accordion,
  Autocomplete,
  DSInputType,
  Info,
  Input,
  LoadingModal,
  type OptionProps,
  type QuotaOptionProps,
  QuotaSelect,
  Search,
  Select,
  SlimButton,
  Typography,
} from 'components'
import {
  channelOptions,
  MaterialType,
  materialTypeOptions,
  OriginOrder,
  OVOrder,
  OVTypes,
  sectorOptions,
  TypesValidations,
  unfoldOptions,
} from 'constants/enums'
import { PAYMENT_TERM_CASH_CODE } from 'constants/paymentTerms'
import type { Cliente } from 'container/CreateOrder'
import type { PaymentMethodsProps } from 'container/CreateOrder/components/TopSection'
import type { QuotaMonth } from 'container/SalesOrder/interfaces'
import { Controller, useFormContext } from 'react-hook-form'
import { useMatch, useParams } from 'react-router-dom'
import { getClientCredit } from 'services/client/credit'
import type { ClientCrediInfo } from 'services/client/credit/interfaces'
import { getCustomers } from 'services/client/list'
import { getClientOvTypes } from 'services/client/ovTypes'
import { getClientAddress, getSaleRegions } from 'services/salesOrder'
import { setIsYTRA } from 'store/OVContext/ovSlice'
import { useDispatch, useSelector } from 'store/store'

import type { ActionPerm } from '../Header/constants'
import * as S from './styles'

const MAX_LIST_SIZE = 10

type OVHeaderProps<T extends OVOrder> = {
  type: T
  postDraft: () => Promise<void>
  quotasMonth: QuotaMonth[]
  defaultClient?: Pick<
    Cliente,
    | 'cd_clie'
    | 'nm_clie'
    | 'cd_empr'
    | 'cd_orgn_vend'
    | 'cd_escr_vend'
    | 'cd_canl_dstb'
    | 'cd_seto_atvd'
    | 'cd_regi_vend'
    | 'ds_regi_vend'
    | 'nr_cnpj_cpf'
  >
  paymentMethods: PaymentMethodsProps[]
  addressOptions: OptionProps[]
  /** @deprecated List will comming from API */
  clientOvTypes?: OptionProps[]
  inputAutocomplete: string
  inputAutocompleteSaleRegion: string
  setAddressOptions: Dispatch<SetStateAction<OptionProps[]>>
  setInputAutocomplete: Dispatch<SetStateAction<string>>
  defaultExpanded: boolean
  loading?: boolean
  disabled?: boolean
  disableClientConfig?: never
  disableFields?: never
  disableOrderConfig?: never
  subHeader?: never
  setClientOvTypes?: never
  setInputAutocompleteSaleRegion?: never
  setSubHeader?: never
  hasOvs?: never
}

type OrderHeaderProps<T extends OVOrder> = {
  type: T
  subHeader: ClientCrediInfo
  setSubHeader: Dispatch<SetStateAction<ClientCrediInfo>>
  clientOvTypes: OptionProps[]
  setClientOvTypes: Dispatch<SetStateAction<OptionProps[]>>
  addressOptions: OptionProps[]
  setAddressOptions: Dispatch<SetStateAction<OptionProps[]>>
  inputAutocomplete: string
  setInputAutocomplete: Dispatch<SetStateAction<string>>
  inputAutocompleteSaleRegion: string
  setInputAutocompleteSaleRegion: Dispatch<SetStateAction<string>>
  disabled?: boolean
  disableClientConfig?: boolean
  disableFields?: boolean
  disableOrderConfig?: boolean
  quotasMonth: QuotaMonth[]
  postDraft: () => Promise<void>
  paymentMethods: PaymentMethodsProps[]
  defaultClient?: Pick<
    Cliente,
    | 'cd_clie'
    | 'nm_clie'
    | 'cd_empr'
    | 'cd_orgn_vend'
    | 'cd_escr_vend'
    | 'cd_canl_dstb'
    | 'cd_seto_atvd'
    | 'cd_regi_vend'
    | 'ds_regi_vend'
    | 'nr_cnpj_cpf'
  >
  loading?: boolean
  defaultExpanded?: boolean
  hasOvs?: boolean
}

export type OVOrderHeaderProps<T extends OVOrder> = T extends OVOrder.ORDER
  ? OrderHeaderProps<T>
  : OVHeaderProps<T>

export const OVOrderHeader = <T extends OVOrder>({
  disableClientConfig,
  disabled = false,
  disableFields,
  disableOrderConfig,
  subHeader,
  setClientOvTypes,
  setInputAutocompleteSaleRegion,
  setSubHeader,
  defaultClient,
  quotasMonth,
  paymentMethods,
  type,
  clientOvTypes,
  addressOptions,
  inputAutocomplete,
  inputAutocompleteSaleRegion,
  setAddressOptions,
  setInputAutocomplete,
  defaultExpanded,
  postDraft,
  loading,
  hasOvs,
}: OVOrderHeaderProps<T>) => {
  const dispatch = useDispatch()
  const { control, setValue, getValues, formState, trigger, watch, reset } = useFormContext()
  const { isDirty } = formState

  const { ovNumber } = useParams()
  const { ovSelected, isYTRA } = useSelector(({ ov }) => ov)
  const { records, isEDI: isEdiOrder } = useSelector((state) => state.salesOrder)
  const { orderStep } = useSelector((store) => store.structure)
  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.OV, action)
  }

  const isOrder = type === OVOrder.ORDER
  const isEDI = isEdiOrder || ovSelected?.Cabecalho?.OrigemPedido === OriginOrder.EDI

  let codigoCanalDistribuicao = ovSelected?.Cabecalho?.CodigoCanalDistribuicao
  const codigoTipoOrdem = ovSelected?.Cabecalho?.CodigoTipoOrdem ?? ''

  const dataToHeader = {
    cd_canl_dstb: ovSelected?.Cabecalho?.CodigoCanalDistribuicao ?? '',
    cd_catg_item: ovSelected?.Cabecalho?.CodigoCategoriaItem ?? '',
    cd_cent_cust: ovSelected?.Cabecalho?.CodigoCentroCusto ?? '',
    cd_cent_matr: ovSelected?.Cabecalho?.CodigoCentroMaterial ?? '',
    cd_clie_entr:
      addressOptions?.find((address) => address.title === ovSelected?.Cabecalho?.EnderecoEntrega)
        ?.value ?? '',
    cd_cond_pgto: ovSelected?.Cabecalho?.CondicaoPagamento?.substring(0, 4) ?? '',
    cd_elem_pep: ovSelected?.Cabecalho?.CodigoElementoPep ?? '',
    cd_inco: ovSelected?.Cabecalho?.Incoterm ?? '',
    cd_mes_fatm: ovSelected?.Cabecalho?.MesFaturamento ?? '',
    cd_mes_pedi: ovSelected?.Cabecalho?.MesPedido ?? '',
    cd_moti_prom: ovSelected?.Cabecalho?.CodigoMotivoPromocional ?? '',
    cd_seto_atvd: ovSelected?.Cabecalho?.Setor?.substring(0, 2) ?? '',
    cd_tipo_orde: codigoTipoOrdem,
    ds_armz: ovSelected?.Cabecalho?.DestinationWarehouse ?? '',
    ds_grup_clie: ovSelected?.Cabecalho?.CanalDistribuicao ?? '',
    dt_ano_mes_cota: ovSelected?.Cabecalho?.MesCota ?? '',
    // fl_data_fixa_entr: ovSelected?.Cabecalho?.FlagDataFixaEntrega ?? TypesValidations.NO,
    fl_cbtr_camb: ovSelected?.Cabecalho?.CoberturaCambial ?? TypesValidations.NO,
    fl_mto_prep:
      ovSelected?.Cabecalho?.TipoMaterial === MaterialType.MTO
        ? TypesValidations.YES
        : TypesValidations.NO,
    fl_prmt_desd: TypesValidations.YES,
    nr_dias_adic: ovSelected?.Cabecalho?.PrazoExtra?.toString() ?? '0',
    nr_insc_estl: ovSelected?.Cabecalho?.InscricaoEstadual ?? '',
    nr_pedi_clie: ovSelected?.Cabecalho?.NumeroPedidoCliente ?? ' ',
    pc_dsct: ovSelected?.Cabecalho?.Desconto ?? 0,
  }
  const defaultValues = { ...getValues(), ...dataToHeader }

  codigoCanalDistribuicao = codigoCanalDistribuicao ?? channelOptions[0].value

  const dt_ano_mes_cota = watch('dt_ano_mes_cota')
  const selectedOvType = watch('cd_tipo_orde')
  const regiaoVenda = watch('cd_regi_vend')
  const channelDist = watch('cd_canl_dstb')

  const isYEXR = useMemo(() => selectedOvType === OVTypes.YEXR, [selectedOvType])

  useEffect(() => {
    dispatch(setIsYTRA(selectedOvType === OVTypes.YTRA))

    if (selectedOvType === OVTypes.YTRA) {
      setValue('cd_cond_pgto', PAYMENT_TERM_CASH_CODE)
    }
  }, [selectedOvType])

  const quotaMonthOptions: QuotaOptionProps[] =
    selectedOvType === OVTypes.YRBA && orderStep === 0
      ? quotasMonth
          .filter((month) => month.fl_ano_mes_cota_antp !== TypesValidations.YES)
          .map((month) => ({
            title: month.ds_ano_mes_cota,
            value: month.dt_ano_mes_cota,
          }))
      : quotasMonth.map((month) => ({
          anticipated: month.fl_ano_mes_cota_antp === TypesValidations.YES,
          title: month.ds_ano_mes_cota,
          value: month.dt_ano_mes_cota,
        }))

  const [loadingClient, setLoadingClient] = useState(false)
  const [clearNumber, setClearNumber] = useState<number>(0)
  const [searchPage, setSearchPage] = useState<number>(0)
  const [searchWidth, setSearchWidth] = useState(0)
  const [shouldSaveQuota, setShouldSaveQuota] = useState(false)
  const [baseKey, setBaseKey] = useState(0)
  const [saleRegions, setSaleRegions] = useState<OptionProps[]>([])
  const [selectedClient, setSelectedClient] = useState({
    cd_clie: '',
    nm_clie: '',
    nr_cnpj_cpf: '',
  })

  const saleRegionsDivRef = useRef<HTMLDivElement>(null)
  const clientDivRef = useRef<HTMLDivElement>(null)
  const sapDivRef = useRef<HTMLDivElement>(null)
  const cnpjDivRef = useRef<HTMLDivElement>(null)
  const searchClientRef = useRef<HTMLDivElement>(null)
  const match = useMatch('/gerenciar/pedido/:orderNumber')
  useLayoutEffect(() => {
    setSearchWidth(searchClientRef?.current?.offsetWidth || 0)
  }, [searchClientRef?.current?.offsetWidth])

  const disableOnOV = (bool?: boolean, ovDis?: boolean) => {
    // NOTE: Follow the priority of importance
    if (disabled) {
      return true
    }

    if (match) {
      return hasOvs || bool
    }

    if (ovNumber) {
      if (ovSelected?.Cabecalho?.CodigoStatus == 0) {
        if (ovDis === undefined) {
          return true
        }
        return ovDis
      }

      return true
    } else {
      return bool
    }
  }

  const disableQuotaMonth = (defaultCondition: boolean) => {
    // NOTE: Follow the priority of importance
    if (disabled) {
      return true
    }

    if (match) {
      return (
        records.length > 0 &&
        !records.some((record) => [0, 20].includes(record.cd_stat_pedi_rasc_item))
      )
    }

    if (ovNumber) {
      if (+ovSelected?.Cabecalho?.CodigoStatus !== 0) {
        return true
      }
    }
    return defaultCondition
  }

  const returnToDefault = () => {
    reset(defaultValues)
    const condition = paymentMethods?.find((pay) => pay.cd_cond_pgto === defaultValues.cd_cond_pgto)

    setInputAutocomplete(
      condition?.ds_cond_pgto ? `${condition.cd_cond_pgto} - ${condition.ds_cond_pgto}` : '',
    )
  }

  const handleNumberInput = (currentValue: string, value: string): string => {
    if (value === '') return '0'
    if (!isValidNumber(value)) return currentValue
    else return `${Number(value)}`
  }

  useEffect(() => {
    if (dt_ano_mes_cota !== '' && shouldSaveQuota) {
      postDraft()
    }
  }, [dt_ano_mes_cota])

  useEffect(() => {
    let isMounted = true

    const fetchSalesRegions = async () => {
      const regionsData = await getSaleRegions(channelDist)
      if (isMounted) {
        let regionsOptions: OptionProps[] = []

        if (regionsData) {
          regionsOptions = regionsData.data
            .filter((regions) => !!regions.cd_regi_vend)
            .map((region) => ({
              title: region.ds_regi_vend,
              value: region.cd_regi_vend,
            }))
        }

        setSaleRegions(regionsOptions)
      }
    }

    setSaleRegions([])
    fetchSalesRegions()

    return () => {
      isMounted = false
    }
  }, [channelDist])

  useEffect(() => {
    if (saleRegions && saleRegions.length === 1) {
      setValue('cd_regi_vend', saleRegions[0].value)
    }
  }, [saleRegions])

  const getListSAPAsync = async (inputValue: string, page: string) => {
    const returnApi = await getCustomers({
      cd_canl_dstb: channelDist,
      cd_clie: inputValue,
      cd_regi_vend: regiaoVenda,
      cd_seto_atvd: getValues('cd_seto_atvd'),
      nr_pagi: page,
      qt_rowcount: MAX_LIST_SIZE,
    })

    return returnApi
  }

  const getListAsync = async (inputValue: string, page: string) => {
    const returnApi = await getCustomers({
      cd_canl_dstb: channelDist,
      cd_regi_vend: regiaoVenda,
      cd_seto_atvd: getValues('cd_seto_atvd'),
      nm_pcro: inputValue,
      nr_pagi: page,
      qt_rowcount: MAX_LIST_SIZE,
    })

    return returnApi
  }

  const getListCNPJAsync = async (inputValue: string, page: string) => {
    const returnApi = await getCustomers({
      cd_canl_dstb: channelDist,
      cd_regi_vend: regiaoVenda,
      cd_seto_atvd: getValues('cd_seto_atvd'),
      nr_cnpj_cpf_pesq: inputValue,
      nr_pagi: page,
      qt_rowcount: MAX_LIST_SIZE,
    })

    return returnApi
  }

  const getDefaultInput = (selectedValue: 'cd_clie' | 'nr_cnpj_cpf' | 'nm_clie') => {
    if (selectedClient && selectedClient[selectedValue] && selectedClient[selectedValue] !== '') {
      return selectedClient[selectedValue]
    }
    if (defaultClient && defaultClient[selectedValue] !== '') {
      return defaultClient[selectedValue]
    }

    return ''
  }

  const handleQuotaChange = (quota: string) => {
    setValue('dt_dsjd_pedi', getLastDayOfMonth(quota, 'YYYY-MM-DD'))

    if (quotaMonthOptions.find((option) => option.value === quota)?.anticipated) {
      setValue('fl_matr_fora_linh', TypesValidations.NO)
    }

    setShouldSaveQuota(true)
  }

  const handleOvTypeChange = (ovType: string) => {
    if (ovType === OVTypes.YRBA) {
      const currentMonth = getValues('dt_ano_mes_cota')
      const validMonths = quotaMonthOptions.filter((month) => !month.anticipated)
      const isQuotaValid = validMonths.find((item) => item.value === currentMonth)

      if (!isQuotaValid) {
        const firstOption = quotaMonthOptions[0]?.value as string

        handleQuotaChange(firstOption)
        setValue('dt_ano_mes_cota', firstOption)
      }
    }
  }

  const handleClientSelect = async (e: Cliente) => {
    setLoadingClient(true)
    setValue('cd_clie', e)
    setValue('cd_cond_pgto', '')
    setValue('address', '')
    setValue('nr_cnpj_cpf', e.nr_cnpj_cpf)
    setValue('fl_data_fixa_entr', TypesValidations.NO)

    const { cd_empr, cd_orgn_vend, cd_canl_dstb, cd_seto_atvd, cd_clie, cd_escr_vend } = e

    await getClientAddress({
      cd_canl_dstb,
      cd_clie,
      cd_empr,
      cd_orgn_vend,
      cd_seto_atvd,
    }).then((response) => {
      if (setAddressOptions)
        setAddressOptions(
          response?.map((add) => ({
            title: add.ds_endr,
            value: add.cd_pcro,
          })),
        )
      if (response.length === 1) {
        setValue('address', response[0].cd_pcro)
      }
    })

    // TODO: Check if necessary. Probably duplicated or bad location
    await getClientOvTypes(cd_clie, cd_escr_vend).then((types) => {
      const ovTypes = types?.map((type: string) => {
        return { title: type, value: type }
      })

      if (!types.includes(selectedOvType)) {
        const hasYPEM = types.includes(OVTypes.YPEM)
        setValue('cd_tipo_orde', hasYPEM ? OVTypes.YPEM : types[0])
      }

      if (setClientOvTypes) setClientOvTypes(ovTypes)
    })

    await getClientCredit(cd_clie).then((credit) => {
      if (setSubHeader) setSubHeader(credit)
    })

    setLoadingClient(false)
    postDraft()
  }

  const toDecimal = (value: string) => numberToDecimalString(+value)

  const clearRegion = () => {
    setValue('cd_regi_vend', '')
    setValue('ds_regi_vend', '')
    if (setInputAutocompleteSaleRegion) setInputAutocompleteSaleRegion('')
  }

  const clearClient = () => {
    setValue('cd_clie', {})
    setValue('nr_cnpj_cpf', '---')
    setValue('nr_insc_estl', '--')
    setValue('address', '')
    if (setAddressOptions) setAddressOptions([])
    setClearNumber(1)
    setTimeout(() => setClearNumber(0), 500)
  }

  const renderSearch = (
    key: string,
    label: string,
    id: string,
    defaultInputValue: 'cd_clie' | 'nm_clie' | 'nr_cnpj_cpf',
    placeHolder: string,
    loadOptions: ((inputValue: string, page: string) => Promise<Cliente[]>) | undefined,
    infoText: React.ReactNode,
    isRightSided?: boolean,
  ) => (
    <Search<Cliente>
      clearInput={clearNumber}
      columnsSizeOption={['100%']}
      defaultInputValue={getDefaultInput(`${defaultInputValue}`)}
      disabled={disableOnOV(disableOrderConfig || !regiaoVenda || isEDI)}
      flexWrapOptionDescription
      hideIcon
      id={id}
      isClientSearch
      isRightSided={isRightSided}
      key={`${key}-${baseKey}`}
      keysColumnsOption={['nm_clie']}
      label={label}
      labelAdornment={<Info iconSize={12}>{infoText}</Info>}
      labelsConcat={[defaultInputValue]}
      listWidth={`${searchWidth}px`}
      loadOnFocus
      loadOptions={loadOptions}
      minimumInput={0}
      optionsLimit={MAX_LIST_SIZE}
      optionsMaxHeight="400px"
      page={searchPage}
      placeHolder={placeHolder}
      setOptionSelected={(e) => {
        setSelectedClient({
          cd_clie: e.cd_clie,
          nm_clie: e.nm_clie,
          nr_cnpj_cpf: e.nr_cnpj_cpf,
        })
        setBaseKey(baseKey + 1)
        handleClientSelect(e)
      }}
      setPage={setSearchPage}
      type="select"
      valueCase="upper"
    />
  )

  return (
    <S.ConfigClient type={type}>
      {loadingClient && <LoadingModal />}

      <Accordion
        defaultExpanded={match ? false : defaultExpanded}
        summaryContent={
          <S.TitleRow>
            <span id="MaterialsInsertTitle">Cabeçalho</span>
          </S.TitleRow>
        }
      >
        <S.OrderConfig>
          <S.FieldWrapper mediaTemplateColumns="1fr" templateColumns="1fr 1fr">
            <S.FieldWrapper mediaTemplateColumns="1.4fr 1fr" templateColumns="1fr 1fr">
              <S.FieldWrapper templateColumns=".1fr .7fr .4fr">
                <div className="fieldItemWrapper">
                  <Controller
                    control={control}
                    name="cd_canl_dstb"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        defaultValue={codigoCanalDistribuicao}
                        disabled={disableOnOV(disableClientConfig || disableFields)}
                        focused={false}
                        id="cd_canl_dstb"
                        label="Mercado"
                        name={name}
                        onChange={(e) => {
                          onChange(e)
                          clearRegion()
                          clearClient()
                        }}
                        options={channelOptions}
                        value={value}
                      />
                    )}
                  />
                </div>
                <div className="fieldItemWrapper">
                  <Controller
                    control={control}
                    name="cd_seto_atvd"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        defaultValue={sectorOptions[0].value}
                        disabled={disableOnOV(disableClientConfig || disableFields)}
                        focused={false}
                        id="cd_seto_atvd"
                        label="Setor"
                        name={name}
                        onChange={(e) => {
                          onChange(e)
                          clearClient()
                        }}
                        options={sectorOptions}
                        value={value}
                      />
                    )}
                  />
                </div>
                <div className="fieldItemWrapper">
                  <Controller
                    control={control}
                    name="fl_mto_prep"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        defaultValue={TypesValidations.NO}
                        disabled={disableOnOV(disableClientConfig)}
                        focused={false}
                        id="tipo-material"
                        label="Tipo de pedido"
                        name={name}
                        onChange={(e) => {
                          onChange(e)
                          if (e.target.value === TypesValidations.YES) {
                            setValue('ds_text_item_mto', '')
                          } else {
                            setValue('ds_text_item_mto', undefined)
                          }
                        }}
                        options={materialTypeOptions}
                        value={value}
                      />
                    )}
                  />
                </div>
              </S.FieldWrapper>
              <div className="fieldItemWrapper regions" ref={saleRegionsDivRef}>
                <Controller
                  control={control}
                  name="cd_regi_vend"
                  render={({ field: { name, ref, onChange }, fieldState: { error } }) => (
                    <Autocomplete<OptionProps>
                      browserAutocomplete="off"
                      data={saleRegions}
                      disabled={disableOnOV(!saleRegions?.length || disableFields)}
                      error={error?.message}
                      input={inputAutocompleteSaleRegion}
                      inputId={name}
                      label="Região de venda"
                      objDescription={'title'}
                      objId={'value'}
                      onInputChange={() => {
                        setValue('cd_regi_vend', '', { shouldDirty: true })
                        setValue('ds_regi_vend', '')
                      }}
                      placeHolder="Clique para selecionar"
                      ref={ref}
                      setInput={
                        setInputAutocompleteSaleRegion ||
                        (() => {
                          return
                        })
                      }
                      setOptionSelected={(e) => {
                        if (e?.value) {
                          onChange(e.value)
                          setValue('ds_regi_vend', e.title)
                          trigger(['cd_regi_vend', 'ds_regi_vend'])
                        }
                      }}
                    />
                  )}
                  rules={{ required: true }}
                />
              </div>
            </S.FieldWrapper>
            <S.FieldWrapper
              mediaTemplateColumns=".3fr .5fr 1.6fr"
              ref={searchClientRef}
              templateColumns=".15fr .25fr .8fr"
            >
              <div className="fieldItemWrapper" id="cod_sap" ref={sapDivRef}>
                {renderSearch(
                  'sap-key',
                  'Código SAP',
                  'cod_sap',
                  'cd_clie',
                  'ˍ ˍ ˍ ˍ ˍ ˍ ˍ',
                  getListSAPAsync,
                  <>
                    Ao preencher este campo, os campos “CPF/CNPJ” e “Razão social” serão
                    automaticamente preenchidos. <br /> <br />
                    As configurações dos campos “Setor” e “Região de venda” podem afetar os
                    resultados disponíveis neste campo.
                  </>,
                )}
              </div>
              <div className="fieldItemWrapper" id="cnpj" ref={cnpjDivRef}>
                {renderSearch(
                  'cnpj-key',
                  'CPF/CNPJ',
                  'cnpj',
                  'nr_cnpj_cpf',
                  'ˍ ˍ.ˍ ˍ ˍ.ˍ ˍ ˍ ˍ/ˍ ˍ ˍ ˍ-ˍˍ',
                  getListCNPJAsync,
                  <>
                    Ao preencher este campo, os campos “Código SAP” e “Razão social” serão
                    automaticamente preenchidos. <br /> <br /> As configurações dos campos “Setor” e
                    “Região de venda” podem afetar os resultados disponíveis neste campo.
                  </>,
                  true,
                )}
              </div>
              <div className="fieldItemWrapper" id="client_name" ref={clientDivRef}>
                {renderSearch(
                  'client-key',
                  'Razão Social',
                  'client',
                  'nm_clie',
                  'Selecione uma região de venda para buscar um cliente',
                  getListAsync,
                  <>
                    Ao preencher este campo, os campos “Código SAP” e “CPF/CNPJ” serão
                    automaticamente preenchidos. <br /> <br />
                    As configurações dos campos “Setor” e “Região de venda” podem afetar os
                    resultados disponíveis neste campo.
                  </>,
                  true,
                )}
              </div>
            </S.FieldWrapper>
          </S.FieldWrapper>
          <S.FieldWrapper mediaTemplateColumns="1fr" templateColumns="1fr 1fr">
            <S.FieldWrapper mediaTemplateColumns="1.4fr 1fr" templateColumns="1fr 1fr">
              <S.FieldWrapper templateColumns=".7fr .4fr">
                <div className="outerFieldItemWrapper">
                  <div className="fieldItemWrapper">
                    <Controller
                      control={control}
                      name="dt_ano_mes_cota"
                      render={({ field: { name, value, onChange } }) => (
                        <QuotaSelect
                          disabled={disableQuotaMonth(
                            disableOrderConfig || !quotaMonthOptions.length,
                          )}
                          label="Mês cota"
                          labelAdornment={
                            <Info
                              iconSize={12}
                            >{`Para preencher este campo é necessário preencher os campos de cliente.`}</Info>
                          }
                          name={name}
                          onChange={(e) => {
                            const valueQuotaMonth = e.target?.value
                            if (typeof valueQuotaMonth === 'string') {
                              handleQuotaChange(valueQuotaMonth)
                            }
                            onChange(e)
                          }}
                          options={quotaMonthOptions}
                          value={value}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="fieldItemWrapper">
                  <Controller
                    control={control}
                    name="fl_prmt_desd"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        disabled={disableOnOV(disableOrderConfig)}
                        focused={false}
                        id="desdobrar"
                        label="Desdobro"
                        labelAdornment={
                          <Info iconSize={12}>
                            {`Ao selecionar a opção "Fábrica", cada fábrica vai gerar a sua
                        Ordem de Venda (OV); Ao selecionar "Material", cada material
                        vai gerar a sua Ordem de Venda (OV).`}
                          </Info>
                        }
                        name={name}
                        onChange={onChange}
                        options={unfoldOptions}
                        value={value}
                      />
                    )}
                  />
                </div>
              </S.FieldWrapper>
              <S.FieldWrapper templateColumns=".7fr .4fr">
                <div className="fieldItemWrapper">
                  <S.LabelInfos>Subcanal</S.LabelInfos>
                  <S.DivStyled>
                    <Typography design="hav-default-03-text" id="subcanal">
                      {getValues('ds_grup_clie')}
                    </Typography>
                  </S.DivStyled>
                </div>

                <div className="fieldItemWrapper">
                  <S.LabelInfos>Insc. estadual</S.LabelInfos>
                  <S.DivStyled>
                    <Typography design="hav-default-03-text" id="nr_insc_estl">
                      {getValues('nr_insc_estl')}
                    </Typography>
                  </S.DivStyled>
                </div>
              </S.FieldWrapper>
            </S.FieldWrapper>
            <S.FieldWrapper mediaTemplateColumns="3fr 1fr" templateColumns="3fr 1fr">
              <div className="fieldItemWrapper">
                {addressOptions.length === 1 ? (
                  <>
                    <S.LabelInfos>Endereço de entrega</S.LabelInfos>
                    <S.DivStyled>
                      <Typography design="hav-default-03-text" id="end-entrega-solo">
                        {addressOptions[0].title}
                      </Typography>
                    </S.DivStyled>
                  </>
                ) : (
                  <Controller
                    control={control}
                    defaultValue=""
                    name="cd_clie_entr"
                    render={({ field: { value, onChange, name } }) => (
                      <Select
                        disabled={
                          !addressOptions.length ||
                          disableOnOV(disableOrderConfig, shouldDisable(ACTIONS_SCREENS.SAVE))
                        }
                        id="end-entrega"
                        label="Endereço de entrega"
                        menuText="Selecione um cliente para buscar endereços"
                        name={name}
                        onChange={(e) => {
                          onChange(e)
                          if (isOrder) postDraft()
                        }}
                        options={addressOptions}
                        value={value}
                      />
                    )}
                  />
                )}
              </div>
            </S.FieldWrapper>
          </S.FieldWrapper>
          <S.FieldWrapper mediaTemplateColumns="1fr" templateColumns="1fr 1fr">
            <S.FieldWrapper templateColumns="1fr 1fr">
              <S.FieldWrapper templateColumns={isOrder ? '1fr' : '.7fr .4fr'}>
                <div className="fieldItemWrapper">
                  <Controller
                    control={control}
                    name="cd_cond_pgto"
                    render={({ field: { onChange, ref, name }, fieldState: { error } }) => (
                      <Autocomplete<PaymentMethodsProps>
                        browserAutocomplete="off"
                        data={paymentMethods}
                        disabled={
                          disableOnOV(disableOrderConfig, shouldDisable(ACTIONS_SCREENS.SAVE)) ||
                          isYTRA ||
                          !clientOvTypes?.length
                        }
                        error={error?.message}
                        input={inputAutocomplete}
                        inputId={name}
                        label="Condições de pagamento"
                        objDescription={'ds_cond_pgto'}
                        objId={'cd_cond_pgto'}
                        onInputChange={() => {
                          setValue('cd_cond_pgto', '', { shouldDirty: true })
                          setValue('ds_cond_pgto', '')
                        }}
                        placeHolder="Clique para selecionar"
                        ref={ref}
                        setInput={setInputAutocomplete}
                        setOptionSelected={(e) => {
                          if (e?.cd_cond_pgto) {
                            onChange(e.cd_cond_pgto)
                            setValue('ds_cond_pgto', e.ds_cond_pgto)
                            trigger(['cd_cond_pgto', 'ds_cond_pgto'])
                          }
                        }}
                        useCodeOnDescription
                      />
                    )}
                    rules={{ required: true }}
                  />
                </div>
                {type === OVOrder.OV && (
                  <div className="fieldItemWrapper">
                    <Controller
                      control={control}
                      name="cd_cent_matr"
                      render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                        <Input
                          disabled={disableOnOV(disableOrderConfig)}
                          error={error?.message}
                          InputProps={{
                            fullWidth: true,
                            placeholder: 'Ex: 0001',
                          }}
                          label="Fábrica"
                          maxLength={2}
                          name={name}
                          onChange={onChange}
                          value={value}
                        />
                      )}
                    />
                  </div>
                )}
              </S.FieldWrapper>
              <S.FieldWrapper templateColumns=".7fr .4fr">
                <div className="fieldItemWrapper">
                  <Controller
                    control={control}
                    name="cd_tipo_orde"
                    render={({ field: { name, value, onChange } }) => (
                      <Select
                        disabled={
                          disableOnOV(disableOrderConfig || disableClientConfig) ||
                          isYTRA ||
                          !clientOvTypes?.length
                        }
                        id="tipo-ov"
                        label="Tipo de OV"
                        name={name}
                        onChange={(e) => {
                          handleOvTypeChange(e.target.value as string)
                          onChange(e)
                        }}
                        options={clientOvTypes}
                        value={value}
                      />
                    )}
                  />
                </div>
                <div className="fieldItemWrapper">
                  <Controller
                    control={control}
                    name="nr_dias_adic"
                    render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                      <Input
                        disabled={
                          isYTRA ||
                          isYEXR ||
                          disableOnOV(disableOrderConfig, shouldDisable(ACTIONS_SCREENS.SAVE))
                        }
                        error={error?.message}
                        InputProps={{
                          endAdornment: 'dias',
                          fullWidth: true,
                          placeholder: 'Ex: 60',
                        }}
                        label="Dias extras"
                        maxLength={2}
                        name={name}
                        onChange={(e) => {
                          const newValue = handleNumberInput(value, e.target.value)
                          onChange(newValue)
                        }}
                        value={value}
                      />
                    )}
                  />
                </div>
              </S.FieldWrapper>
            </S.FieldWrapper>
            <div className="fieldItemWrapper">
              <Controller
                control={control}
                defaultValue=""
                name="nr_pedi_clie"
                render={({ field: { name, value, onChange } }) => (
                  <Input
                    design={DSInputType.SEARCH_INPUT}
                    disabled={disableOnOV(disableOrderConfig, shouldDisable(ACTIONS_SCREENS.SAVE))}
                    InputProps={{
                      fullWidth: true,
                    }}
                    id="pedi-clie"
                    label="N° de pedido do cliente"
                    labelAdornment={'(opcional)'}
                    maxLenghtPosition="top"
                    maxLength={20}
                    name={name}
                    onChange={(e) => {
                      onChange(e)
                    }}
                    showMaxLength
                    type="text"
                    value={value as string}
                  />
                )}
              />
            </div>
          </S.FieldWrapper>
          {getValues('fl_mto_prep') === TypesValidations.YES && isOrder && (
            <>
              <S.FieldWrapper mediaTemplateColumns="1fr" templateColumns="1fr">
                <div>
                  <Controller
                    control={control}
                    defaultValue={''}
                    name="ds_text_item_mto"
                    render={({ field: { name, value, onChange, ref }, fieldState: { error } }) => (
                      <Input
                        disabled={disableOnOV(disableOrderConfig)}
                        error={error?.message}
                        InputProps={{
                          inputProps: { maxLength: 255 },
                          placeholder: 'Digite aqui a informação necessária',
                        }}
                        id="MTODescription"
                        label="Informação para expedição"
                        labelAdornment={
                          <S.LabelWrapper>
                            <span>(Exibido por pedido MTO)</span>
                            <Info iconSize={12}>
                              Ao selecionar o tipo de pedido como “MTO” (Make To Order), se torna
                              necessário preencher este campo detalhando as especificidades exigidas
                              na preparação do produto, como os dados exibidos nas caixas dos
                              materiais ou dados de etiqueta.
                            </Info>
                          </S.LabelWrapper>
                        }
                        maxLenghtPosition="top"
                        maxLength={255}
                        name={name}
                        onChange={onChange}
                        ref={ref}
                        required
                        rows={1}
                        showMaxLength
                        type="text"
                        value={value}
                      />
                    )}
                  />
                </div>
              </S.FieldWrapper>
            </>
          )}
          {type === OVOrder.OV && isDirty && (
            <>
              <S.FieldWrapper mediaTemplateColumns="1fr" templateColumns="1fr">
                <S.FormActions>
                  <SlimButton
                    content="Salvar"
                    disabled={loading || disabled}
                    loading={loading}
                    onClick={() => postDraft()}
                    type="button"
                    variant="primary"
                  />
                  <SlimButton
                    content="Desfazer"
                    disabled={loading || disabled}
                    onClick={() => returnToDefault()}
                    type="button"
                    variant="secondary"
                  />
                </S.FormActions>
              </S.FieldWrapper>
            </>
          )}

          {subHeader && Object.keys(subHeader).length > 0 && isOrder && (
            <>
              <S.Divider />
              <S.CreditRow>
                <S.CreditItem>
                  <S.LabelInfos>Limite de crédito</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_limt_cred)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Compromissado</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_tota_comp)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Saldo disponível</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_sald_disp)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Comp. especial</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_cprm_espl)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Total de vendas</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_tota_vend)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Total a receber</S.LabelInfos>
                  <Typography>{toDecimal(subHeader.vl_tota_arec)}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Utilização</S.LabelInfos>
                  <Typography>{`${toDecimal(subHeader.pc_grau_utlz)} %`}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Classe de risco</S.LabelInfos>
                  <Typography>{subHeader.ds_clae_risc}</Typography>
                </S.CreditItem>
                <S.CreditItem>
                  <S.LabelInfos>Venc. limite de créd</S.LabelInfos>
                  <Typography>{subHeader.dt_prox_verf}</Typography>
                </S.CreditItem>
              </S.CreditRow>
            </>
          )}
        </S.OrderConfig>
      </Accordion>
    </S.ConfigClient>
  )
}
